/**
 * MegaMenuV2 - Taxonomy-Driven MegaMenu
 * 
 * Replaces hardcoded MegaMenu with dynamic navigation based on taxonomy.ts.
 * Feature flag: USE_MEGAMENU_V2
 */

import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { 
  Shirt, 
  Gem, 
  ShoppingBag, 
  Home,
  Footprints,
  Moon,
  Sparkles
} from "lucide-react";
import { Z_INDEX } from "@/constants/ui.constants";
import { 
  ROOT_TAXONOMY,
  TaxonomyNode,
  APPAREL_TAXONOMY,
  DRESSES_TAXONOMY,
  ONE_PIECES_TAXONOMY,
  LOUNGEWEAR_TAXONOMY,
  JEWELRY_TAXONOMY,
  ACCESSORIES_TAXONOMY,
  BAGS_TAXONOMY,
  FOOTWEAR_TAXONOMY,
  LIFESTYLE_DECOR_TAXONOMY
} from "@/config/taxonomy";

interface MegaMenuV2Props {
  isOpen: boolean;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

// Icon mapping for collections
const COLLECTION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'apparel': Shirt,
  'jewelry': Gem,
  'accessories': ShoppingBag,
  'bags': ShoppingBag,
  'footwear': Footprints,
  'loungewear': Moon,
  'dresses': Sparkles,
  'one-pieces': Sparkles,
  'lifestyle-decor': Home,
};

// Reusable className strings
const SECTION_LINK_CLASS = "flex items-center gap-2 pb-2 border-b border-border group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm";
const NAV_LINK_CLASS = "block text-muted-foreground hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm px-2 py-1";

/**
 * Render a collection section with its children
 */
function CollectionSection({ 
  node, 
  onClose,
  maxItems = 6 
}: { 
  node: TaxonomyNode; 
  onClose: () => void;
  maxItems?: number;
}) {
  const Icon = COLLECTION_ICONS[node.handle] || Sparkles;
  const collectionUrl = `/collections/${node.collectionHandle || node.handle}`;
  
  // Get direct children (productTypes or categories)
  let children = node.children || [];
  
  // If collection has single productType child with same name, show its children instead
  // (e.g., Dresses > Dresses > [Maxi, Midi, Mini] → show [Maxi, Midi, Mini])
  if (children.length === 1 && children[0].type === 'productType' && children[0].children?.length) {
    children = children[0].children;
  }
  
  const displayChildren = children.slice(0, maxItems);
  const hasMore = children.length > maxItems;

  return (
    <div className="space-y-3">
      <Link 
        to={collectionUrl}
        className={SECTION_LINK_CLASS}
        onClick={onClose}
      >
        <Icon className="h-5 w-5 text-primary" />
        <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
          {node.label}
        </h3>
      </Link>
      <nav className="space-y-0">
        {displayChildren.map(child => {
          // Build URL based on node type using tax* params that ProductGridManager expects
          // Include parent context in URL for proper hierarchy
          let childUrl = collectionUrl;
          
          if (child.type === 'productType') {
            childUrl = `${collectionUrl}?taxProductType=${child.handle}`;
          } else if (child.type === 'category') {
            // Categories need their parent productType context if available
            const parentProductType = node.type === 'productType' ? node.handle : undefined;
            if (parentProductType) {
              childUrl = `${collectionUrl}?taxProductType=${parentProductType}&taxCategory=${child.handle}`;
            } else {
              childUrl = `${collectionUrl}?taxCategory=${child.handle}`;
            }
          } else if (child.type === 'vendor') {
            // Vendor nodes link to their dedicated collection pages
            // Map vendor handles to collection slugs
            const vendorCollectionMap: Record<string, string> = {
              'hot-chocolate-design': '/collections/hot-chocolate-designs',
              'colorstep-fashion': '/collections/colorstep-fashion',
            };
            childUrl = vendorCollectionMap[child.handle] || `${collectionUrl}?taxTags=${child.handle}`;
          } else if (child.type === 'tag') {
            // Tags need their parent productType and category context
            // For flat structures (like Dresses > Maxi Dresses), the parent is the productType
            if (node.type === 'productType') {
              childUrl = `${collectionUrl}?taxProductType=${node.handle}&taxTags=${child.handle}`;
            } else if (node.type === 'category') {
              // If parent is category, we need grandparent productType too
              childUrl = `${collectionUrl}?taxCategory=${node.handle}&taxTags=${child.handle}`;
            } else {
              // Flat structure like Jewelry > Necklaces
              childUrl = `${collectionUrl}?taxTags=${child.handle}`;
            }
          }
          
          return (
            <Link
              key={child.id}
              to={childUrl}
              className={NAV_LINK_CLASS}
              onClick={onClose}
            >
              {child.label}
            </Link>
          );
        })}
        {hasMore && (
          <Link
            to={collectionUrl}
            className={`${NAV_LINK_CLASS} text-primary`}
            onClick={onClose}
          >
            View All →
          </Link>
        )}
      </nav>
    </div>
  );
}

export function MegaMenuV2({ isOpen, onClose, onMouseEnter, onMouseLeave }: MegaMenuV2Props) {
  if (!isOpen) return null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return createPortal(
    <>
      {/* Backdrop overlay */}
      <div 
        className="fixed left-0 right-0 bottom-0 top-20 bg-background/20 backdrop-blur-sm" 
        style={{ zIndex: Z_INDEX.MEGA_MENU - 1 }}
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Menu container */}
      <div 
        className="fixed left-0 right-0 top-20"
        style={{ zIndex: Z_INDEX.MEGA_MENU }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-label="Collections menu"
        aria-modal="false"
      >
        <div className="mx-auto px-4 w-full max-w-[90vw]">
          <div className="bg-card border border-border rounded-lg shadow-lg p-8 w-full">
            <div className="grid grid-cols-3 gap-12">
              
              {/* Left Column: Jewelry, Accessories, Bags */}
              <div className="space-y-6">
                <CollectionSection node={JEWELRY_TAXONOMY} onClose={onClose} />
                <CollectionSection node={ACCESSORIES_TAXONOMY} onClose={onClose} />
                <CollectionSection node={BAGS_TAXONOMY} onClose={onClose} />
              </div>

              {/* Center Column: Apparel, Dresses, One-Pieces */}
              <div className="space-y-6">
                {/* Apparel section with Plus link added */}
                <div className="space-y-3">
                  <Link 
                    to="/collections/apparel"
                    className={SECTION_LINK_CLASS}
                    onClick={onClose}
                  >
                    <Shirt className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                      Apparel
                    </h3>
                  </Link>
                  <nav className="space-y-0">
                    {APPAREL_TAXONOMY.children?.slice(0, 5).map(child => {
                      const childUrl = `/collections/apparel?taxProductType=${child.handle}`;
                      return (
                        <Link
                          key={child.id}
                          to={childUrl}
                          className={NAV_LINK_CLASS}
                          onClick={onClose}
                        >
                          {child.label}
                        </Link>
                      );
                    })}
                    {/* Plus link pointing to Goddess collection */}
                    <Link
                      to="/collections/goddess"
                      className={NAV_LINK_CLASS}
                      onClick={onClose}
                    >
                      Plus
                    </Link>
                  </nav>
                </div>
                <CollectionSection node={DRESSES_TAXONOMY} onClose={onClose} />
                <CollectionSection node={ONE_PIECES_TAXONOMY} onClose={onClose} />
              </div>

              {/* Right Column: Loungewear, Footwear, Lifestyle & Decor */}
              <div className="space-y-6">
                <CollectionSection node={LOUNGEWEAR_TAXONOMY} onClose={onClose} />
                <CollectionSection node={FOOTWEAR_TAXONOMY} onClose={onClose} />
                <CollectionSection node={LIFESTYLE_DECOR_TAXONOMY} onClose={onClose} maxItems={7} />
              </div>

            </div>

            {/* View All Collections CTA */}
            <div className="mt-6 pt-4 border-t border-border text-center">
              <Link 
                to="/collections" 
                className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
                onClick={onClose}
              >
                View All Collections
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
